import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Avatar,
  Chip,
  Icon,
  TextField,
  Grid,
  Divider,
} from "@mui/material";
import Africastalking from "africastalking-client";

export default function CallCenterDialer({
  agentRef,
  contact,
  onIncomingCall,
  onCallCompleted,
  onSaveContact,
  onFetchCallerId,
}) {
  const [state, setState] = useState("notready");
  const [isReady, setIsReady] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [timer, setTimer] = useState(0);
  const [wakeLock, setWakeLock] = useState(null);
  const [micReady, setMicReady] = useState(false);
  const [callerName, setCallerName] = useState("");

  const ringtoneRef = useRef(null);
  const clientRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(0);
  const directionRef = useRef(null); // "inbound" | "outbound"
  const STORAGE_KEY = "ldr_af_webrtc_token";
  const contactRef = useRef(null);
  const callWasConnectedRef = useRef(false);
  const activeNumberRef = useRef("");

  const isUnknownNumber =
    phoneNumber &&
    (!callerName || callerName === phoneNumber) &&
    (!contactRef.current?.name || contactRef.current?.name === phoneNumber);

  let reconnectTimeout;

  window.addEventListener("unhandledrejection", (event) => {
    console.error("UNHANDLED PROMISE:", event.reason);
  });

  window.addEventListener("error", (event) => {
    console.error("GLOBAL ERROR:", event.error);
  });

  async function init() {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      setMicReady(true);

      setupClient(agentRef);
    } catch (err) {
      console.error(err);

      setState("micdenied");
    }
  }

  function setupClient(validToken) {
    if (clientRef.current) {
      try {
        clientRef.current.destroy?.();
      } catch {}
    }

    const client = new Africastalking.Client(validToken);
    clientRef.current = client;

    clientRef.current.on("ready", () => {
      console.log("initDone:", clientRef.current.initDone);
      setState("ready");
      setIsReady(true);
    });

    clientRef.current.on("notready", () => {
      setState("notready");
      setIsReady(false);
    });

    clientRef.current.on("calling", () => setState("calling"));

    // clientRef.current.on("incomingcall", () => {
    //   directionRef.current = "inbound";
    //   setState("incomingcall");
    //   ringtoneRef.current?.play().catch(() => {});

    //   onIncomingCall?.();
    // });

    clientRef.current.on("incomingcall", async (params) => {
      const callerNum = params?.from;

      directionRef.current = "inbound";
      activeNumberRef.current = callerNum || "";

      callWasConnectedRef.current = false;

      setPhoneNumber(callerNum || "");
      setCallerName(callerNum || "");

      contactRef.current = {
        phone_number: callerNum,
        name: callerNum,
      };

      setState("incomingcall");

      ringtoneRef.current?.play().catch(() => {});

      onIncomingCall?.(callerNum);

      if (onFetchCallerId && callerNum) {
        try {
          const resolvedName = await onFetchCallerId(callerNum);

          if (resolvedName) {
            setCallerName(resolvedName);

            contactRef.current = {
              phone_number: callerNum,
              name: resolvedName,
            };
          }
        } catch (error) {
          console.error("Error fetching caller ID:", error);
        }
      }
    });

    clientRef.current.on("callaccepted", () => {
      callWasConnectedRef.current = true;
      setState("callaccepted");
      ringtoneRef.current?.pause();
      if (ringtoneRef.current) ringtoneRef.current.currentTime = 0;
    });

    clientRef.current.on("hangup", () => {
      // if (directionRef.current === "outbound") {
      //   onCallCompleted?.(contactRef.current);
      // }

      if (callWasConnectedRef.current) {
        onCallCompleted?.(contactRef.current);
      }

      setState("hangup");
      ringtoneRef.current?.pause();
      if (ringtoneRef.current) ringtoneRef.current.currentTime = 0;

      setTimeout(() => setState("ready"), 1500);
    });

    clientRef.current.on("offline", reconnect);
    clientRef.current.on("closed", reconnect);
  }

  function reconnect() {
    setState("offline");
    setIsReady(false);

    clearTimeout(reconnectTimeout);
    reconnectTimeout = setTimeout(() => {
      init();
    }, 2000);
  }

  useEffect(() => {
    if (!contact?.phone_number) {
      setPhoneNumber(null);
      contactRef.current = null;
      return;
    }

    setPhoneNumber(contact.phone_number);
    setCallerName(contact.name || contact.phone_number);
    contactRef.current = contact;
    handleCall(contact.phone_number);
  }, [contact]);

  useEffect(() => {
    ringtoneRef.current = new Audio("/audio/002.mp3");
    ringtoneRef.current.loop = true;
  }, []);

  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);

  // =========================
  // WAKE LOCK
  // =========================
  const requestWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) {
        const lock = await navigator.wakeLock.request("screen");
        setWakeLock(lock);
      }
    } catch {}
  };

  const releaseWakeLock = async () => {
    if (wakeLock) {
      await wakeLock.release();
      setWakeLock(null);
    }
  };

  useEffect(() => {
    if (state === "calling" || state === "callaccepted") {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
  }, [state]);

  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        (state === "calling" || state === "callaccepted")
      ) {
        requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [state]);

  // =========================
  // TIMER
  // =========================
  useEffect(() => {
    let interval;
    if (state === "callaccepted") {
      interval = setInterval(() => setTimer((t) => t + 1), 1000);
    } else {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [state]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // =========================
  // ACTIONS (GUARDED ONLY)
  // =========================
  const handleCall = async (number = null) => {
    if (!isReady) return;

    const dialNumber = number || phoneNumber;
    if (!dialNumber || !clientRef.current) return;

    directionRef.current = "outbound";
    activeNumberRef.current = dialNumber;
    callWasConnectedRef.current = false;

    setTimeout(() => {
      clientRef.current.call(dialNumber);
    }, 300);
  };

  const handleHangup = () => {
    if (!isReady) return;
    clientRef.current.hangup();
  };

  const handleAccept = () => {
    if (!isReady) return;
    clientRef.current.answer();
  };

  const handleReject = () => {
    if (!isReady) return;
    clientRef.current.hangup();
  };

  const config = {
    ready: { label: "Ready", color: "success", icon: "call" },
    notready: { label: "Not Ready", color: "default", icon: "phone_disabled" },
    calling: { label: "Calling...", color: "success", icon: "call" },
    incomingcall: {
      label: "Incoming Call",
      color: "info",
      icon: "call_received",
    },
    callaccepted: { label: "In Call", color: "success", icon: "call" },
    hangup: { label: "Call Ended", color: "error", icon: "call_end" },
    offline: { label: "Offline", color: "error", icon: "wifi_off" },
    closed: { label: "Disconnected", color: "default", icon: "wifi_off" },
  }[state];

  if (!micReady) {
    return (
      <Box p={3}>
        <Stack spacing={3}>
          <Box align="center">
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: "warning.main",
              }}
            >
              <Icon sx={{ fontSize: 36 }}>mic</Icon>
            </Avatar>
          </Box>

          <Box textAlign="center">
            <Typography variant="h6" fontWeight={700}>
              Microphone Access
            </Typography>

            <Typography variant="body2" color="text.secondary" mt={1}>
              Microphone permission is required to make and receive calls.
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={<Icon>mic</Icon>}
            onClick={init}
          >
            Enable Microphone
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 420, mx: "auto" }}>
      <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
        <Box mt={1}>
          {["offline", "closed", "notready"].includes(state) ? (
            <Chip
              label={`Status: ${config.label}`}
              color={config.color}
              // deleteIcon={<Icon>refresh</Icon>}
            />
          ) : (
            <Chip label={`Status: ${config.label}`} color={config.color} />
          )}
        </Box>
        {/* {contact && state !== "incomingcall" && (
          <Box mt={3}>
            <Typography variant="h6" fontSize={16}>
              {contact.name}
            </Typography>
          </Box>
        )} */}

        {(contactRef.current || callerName) && (
          <Box mt={3}>
            <Typography variant="h6" fontSize={16}>
              {callerName || contactRef.current?.name || phoneNumber}
            </Typography>

            {callerName && phoneNumber && callerName !== phoneNumber && (
              <Typography variant="caption" color="text.secondary">
                {phoneNumber}
              </Typography>
            )}
          </Box>
        )}
        <Box mt={2} mb={3}>
          <Divider />
        </Box>
        <Stack spacing={2} alignItems="center">
          {state === "callaccepted" && (
            <Typography color="success.main">{formatTime(timer)}</Typography>
          )}

          {/* OUTGOING */}
          {state === "calling" && (
            <Stack spacing={1} alignItems="center">
              <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                <Icon>person</Icon>
              </Avatar>

              <Icon sx={{ animation: "moveUp 1s infinite" }}>arrow_upward</Icon>

              <Avatar sx={{ width: 56, height: 56, bgcolor: "success.main" }}>
                <Icon>call</Icon>
              </Avatar>
            </Stack>
          )}

          {/* INCOMING */}
          {state === "incomingcall" && (
            <Stack spacing={1} alignItems="center">
              <Avatar sx={{ width: 56, height: 56, bgcolor: "success.main" }}>
                <Icon>call</Icon>
              </Avatar>

              <Icon sx={{ animation: "moveDown 1s infinite" }}>
                arrow_downward
              </Icon>

              <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                <Icon>support_agent</Icon>
              </Avatar>

              <Typography mt={1} variant="body2">
                {config.label}
              </Typography>
            </Stack>
          )}

          {/* CONNECTED */}
          {state === "callaccepted" && (
            <Box sx={{ position: "relative", width: 80, height: 80 }}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  transform: "translate(-50%, -50%)",
                  animation: "ripple 2s infinite",
                  opacity: 0.2,
                }}
              />
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "success.main",
                  color: "#fff",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <Icon>call</Icon>
              </Avatar>
            </Box>
          )}

          {/* DEFAULT */}
          {state !== "calling" &&
            state !== "incomingcall" &&
            state !== "callaccepted" && (
              <Avatar sx={{ width: 64, height: 64 }}>
                <Icon>{config.icon}</Icon>
              </Avatar>
            )}

          <Stack spacing={1} width="100%">
            <Box mt={3}>
              {state === "ready" && phoneNumber && (
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  mt={2}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      handleCall();
                    }}
                    disabled={!isReady}
                    startIcon={<Icon>call</Icon>}
                  >
                    Call Again
                  </Button>

                  {callWasConnectedRef.current && isUnknownNumber && (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="info"
                      startIcon={<Icon>person_add</Icon>}
                      onClick={() => onSaveContact?.(activeNumberRef.current)}
                    >
                      Save Contact
                    </Button>
                  )}
                </Stack>
              )}

              {state === "calling" && (
                <Button
                  variant="contained"
                  fullWidth
                  color="error"
                  onClick={handleHangup}
                >
                  Cancel
                </Button>
              )}

              {state === "incomingcall" && (
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      onClick={handleAccept}
                    >
                      Accept
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      color="error"
                      onClick={handleReject}
                    >
                      Reject
                    </Button>
                  </Grid>
                </Grid>
              )}

              {state === "callaccepted" && (
                <Button
                  variant="contained"
                  fullWidth
                  color="error"
                  onClick={handleHangup}
                >
                  Hang Up
                </Button>
              )}
            </Box>
          </Stack>
        </Stack>
      </Box>

      <style>
        {`
          @keyframes moveUp {
            0% { transform: translateY(10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(-10px); opacity: 0; }
          }

          @keyframes moveDown {
            0% { transform: translateY(-10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(10px); opacity: 0; }
          }

          @keyframes ripple {
            0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.4; }
            70% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
            100% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
}
